import React, { useState } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import {
  LayoutGrid, Store, Package, ClipboardList, Star, Megaphone, Ticket, BarChart3,
  MessageSquare, Wallet, Users, CreditCard, Bell, Settings, LifeBuoy, Lock, Volume2, VolumeX, Pencil, Trash2,
} from 'lucide-react';
import { ImageUpload } from '../components/commerce/ImageUpload';
import { AppImage } from '../components/commerce/AppImage';
import { AccountSettings } from '../components/commerce/AccountSettings';
import { Support } from './MyKelatePage';
import { fileUrl } from '../lib/upload';
import { useApp, soundEnabled, setSoundEnabled } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { CenterLayout } from '../components/commerce/CenterLayout';
import { KpiCard } from '../components/commerce/KpiCard';
import { StatusBadge } from '../components/commerce/StatusBadge';
import { PlanBadge } from '../components/commerce/PlanBadge';
import { EmptyState } from '../components/commerce/EmptyState';
import { fmtRM2, timeAgo, PLANS, CATEGORIES } from '../data/mockData';
import { playTest, VOLUME_LEVELS } from '../lib/sound';
import { Switch } from '../components/ui/switch';
import { TopUpForm } from '../components/commerce/TopUpForm';
import { toast } from 'sonner';

function useMyMerchant() {
  const { user } = useAuth();
  const { customMerchants, merchantProfiles } = useApp();
  const base = customMerchants.find((m) => m.id === user?.merchantId);
  if (!base) {
    return { id: user?.merchantId || 'unknown', slug: '', name: user?.name || 'My Store', plan: 'LIST', category: 'Others', categories: [], district: 'Kota Bharu', verified: false, rating: 0, followers: 0, logo: '', banner: '', whatsapp: '', phone: '', address: '', hours: '-', bank: { holder: user?.name || '-', bank: '-', masked: '******0000' }, description: '' };
  }
  return { ...base, ...(merchantProfiles[base.id] || {}) };
}

function Dashboard() {
  const { orders, payouts, unreadCount } = useApp();
  const merchant = useMyMerchant();
  const mine = orders.filter((o) => o.merchantId === merchant.id);
  const newOrders = mine.filter((o) => o.orderStatus === 'PENDING').length;
  const sales = mine.filter((o) => !['CANCELLED'].includes(o.orderStatus)).reduce((s, o) => s + o.itemSubtotal, 0);
  const pendingPayout = payouts.filter((p) => p.merchantId === merchant.id && ['READY', 'PROCESSING'].includes(p.status)).reduce((s, p) => s + p.net, 0);
  const paidOut = payouts.filter((p) => p.merchantId === merchant.id && p.status === 'PAID').reduce((s, p) => s + p.net, 0);

  return (
    <div data-testid="merchant-dashboard" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
        <div className="flex items-center gap-3">
          {merchant.logo ? (
            <img src={fileUrl(merchant.logo)} alt="" className="h-12 w-12 rounded-md border border-kelate-line object-cover" />
          ) : (
            <span className="flex h-12 w-12 items-center justify-center rounded-md bg-kelate-darkwood font-display text-lg font-semibold text-kelate-surface">{merchant.name[0]}</span>
          )}
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-kelate-ink">{merchant.name}</h2>
              <PlanBadge plan={merchant.plan} />
            </div>
            <div className="text-xs text-kelate-soft">{merchant.district} · {PLANS[merchant.plan].commission * 100}% commission · KELATE handles delivery & collection</div>
          </div>
        </div>
        <Link to="/merchant-center/subscription" data-testid="manage-plan-btn" className="rounded-sm border border-kelate-darkwood px-4 py-2 text-sm font-medium text-kelate-darkwood hover:bg-secondary">Manage Plan</Link>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="New Orders" value={newOrders} sub="Needs your action" icon={ClipboardList} testId="kpi-new-orders" />
        <KpiCard label="Sales (GMV)" value={fmtRM2(sales)} sub="All time" icon={BarChart3} testId="kpi-sales" />
        <KpiCard label="Pending Settlement" value={fmtRM2(pendingPayout)} sub="KELATE is collecting" icon={Wallet} testId="kpi-pending-settlement" />
        <KpiCard label="Paid Out" value={fmtRM2(paidOut)} sub="To your bank" icon={CreditCard} testId="kpi-paid-out" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-kelate-ink">Recent Orders</h3>
            <Link to="/merchant-center/orders" className="text-xs font-medium text-kelate-wood">View all</Link>
          </div>
          <div className="space-y-2">
            {mine.slice(0, 5).map((o) => (
              <div key={o.id} className="flex items-center justify-between rounded-sm border border-kelate-line/70 px-3 py-2 text-sm">
                <div>
                  <span className="font-medium text-kelate-ink">#{o.orderNumber}</span>
                  <span className="ml-2 text-xs text-kelate-soft">{o.items.map((i) => i.name).join(', ')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-kelate-darkwood">{fmtRM2(o.totalCustomerDue)}</span>
                  <StatusBadge status={o.orderStatus} />
                </div>
              </div>
            ))}
            {mine.length === 0 && <p className="py-6 text-center text-sm text-kelate-soft">No orders yet.</p>}
          </div>
        </section>
        <section className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
          <h3 className="mb-3 text-sm font-semibold text-kelate-ink">Store Health</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-kelate-soft">Store views (7d)</span><span className="font-medium text-kelate-ink">1,204</span></div>
            <div className="flex justify-between"><span className="text-kelate-soft">Product views (7d)</span><span className="font-medium text-kelate-ink">3,891</span></div>
            <div className="flex justify-between"><span className="text-kelate-soft">WhatsApp clicks (7d)</span><span className="font-medium text-kelate-ink">86</span></div>
            <div className="flex justify-between"><span className="text-kelate-soft">Rating</span><span className="font-medium text-kelate-ink">{merchant.rating} / 5</span></div>
            <div className="flex justify-between border-t border-kelate-line pt-3"><span className="text-kelate-soft">Unread notifications</span><span className="font-medium text-kelate-red">{unreadCount('merchant', merchant?.id)}</span></div>
          </div>
          <div className="mt-4 rounded-sm bg-secondary/50 p-3 text-xs text-kelate-soft">
            Marketing tip: BOOST plan adds promotions, vouchers and featured placement. <Link to="/merchant-center/subscription" className="font-medium text-kelate-wood">Upgrade</Link>
          </div>
        </section>
      </div>
    </div>
  );
}

function MerchantOrders() {
  const { orders, merchantOrderAction } = useApp();
  const merchant = useMyMerchant();
  const mine = orders.filter((o) => o.merchantId === merchant.id);
  const ACTION = {
    PENDING: { label: 'Accept Order', testId: 'accept' },
    ACCEPTED: { label: 'Start Preparing', testId: 'prepare' },
    PREPARING: { label: 'Done — Prepared', testId: 'ready' },
  };
  return (
    <div data-testid="merchant-orders" className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-kelate-ink">Orders</h2>
        <span className="text-xs text-kelate-soft">KELATE delivers & collects payment. You just prepare.</span>
      </div>
      {mine.length === 0 && <EmptyState icon={ClipboardList} title="No orders yet" description="New orders will appear here with a sound alert." />}
      {mine.map((o) => (
        <div key={o.id} data-testid={`merchant-order-${o.orderNumber}`} className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <span className="text-sm font-semibold text-kelate-ink">Order #{o.orderNumber}</span>
              <span className="ml-2 text-xs text-kelate-soft">{timeAgo(o.createdAt)}</span>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={o.orderStatus} />
              <StatusBadge status={o.paymentStatus} />
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            {o.items.map((i, idx) => (
              <div key={idx} className="flex items-center gap-2.5 text-sm">
                <AppImage src={i.image} alt="" className="h-9 w-9 rounded-sm border border-kelate-line bg-white object-contain" />
                <span className="flex-1 text-kelate-ink">{i.name} ×{i.qty}</span>
                <span className="font-medium text-kelate-darkwood">{fmtRM2(i.price * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 grid gap-1 border-t border-kelate-line pt-3 text-xs text-kelate-soft sm:grid-cols-2">
            <span>Customer: {o.customerName} · {o.customerPhone}</span>
            <span>Delivery: KELATE · {o.zone}</span>
            <span>Payment: <b className="text-kelate-ink">{o.paymentMethod === 'WALLET' ? 'KELATE Wallet — PAID' : `${o.paymentMethod === 'TNG_QR' ? 'TNG QR' : o.paymentMethod === 'BANK_TRANSFER' ? 'Bank Transfer' : 'Cash'} — PAY ON DELIVERY`}</b></span>
            <span>Order total: <b className="text-kelate-darkwood">{fmtRM2(o.totalCustomerDue)}</b> (items {fmtRM2(o.itemSubtotal)} + delivery {fmtRM2(o.deliveryFee)})</span>
            {o.referralDiscount > 0 && <span data-testid={`referral-line-${o.orderNumber}`}>Referral ({o.referralCode}): <b className="text-kelate-sage">-{fmtRM2(o.referralDiscount)} funded by KELATE</b></span>}
          </div>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-kelate-soft">Your net: {fmtRM2(o.merchantNet)} (after {o.commissionRate * 100}% commission)</span>
            {ACTION[o.orderStatus] ? (
              <button
                data-testid={`order-action-${ACTION[o.orderStatus].testId}-${o.orderNumber}`}
                onClick={() => merchantOrderAction(o.id)}
                className="rounded-sm bg-kelate-darkwood px-4 py-2 text-sm font-medium text-kelate-surface hover:bg-kelate-wood"
              >
                {ACTION[o.orderStatus].label}
              </button>
            ) : (
              <span className="text-xs text-kelate-sage">
                {o.orderStatus === 'READY_FOR_PICKUP' ? 'Done preparing — waiting for KELATE rider pickup' : ['PICKED_UP', 'OUT_FOR_DELIVERY'].includes(o.orderStatus) ? 'With KELATE delivery' : o.orderStatus === 'DELIVERED' ? 'Delivered — settlement in progress' : o.orderStatus}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function MerchantProducts() {
  const merchant = useMyMerchant();
  const { productsByMerchant, addProduct, updateProduct, removeProduct } = useApp();
  const plan = PLANS[merchant.plan];
  const products = productsByMerchant(merchant.id);
  const [showEditor, setShowEditor] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const blankForm = { name: '', price: '', stock: '', variants: '', category: CATEGORIES[0], image: '', description: '', photos: [], variantOptions: [] };
  const [form, setForm] = useState(blankForm);
  const usage = products.length;
  const reset = () => {
    setForm(blankForm);
    setEditingId(null);
    setShowEditor(false);
  };
  const openAdd = () => { setEditingId(null); setForm(blankForm); setShowEditor(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    const variantOptions = (p.variantOptions?.length ? p.variantOptions : (p.variants || []).map((n) => ({ name: n, price: null })));
    setForm({ name: p.name, price: p.price, stock: p.stock, variants: (p.variants || []).join(', '), category: p.category, image: p.image || '', description: p.description || '', photos: p.photos?.length ? p.photos : (p.image ? [p.image] : []), variantOptions });
    setShowEditor(true);
  };
  const setPhoto = (idx, url) => {
    const photos = [...(form.photos || [])];
    photos[idx] = url;
    setForm({ ...form, photos, image: photos[0] || '' });
  };
  const removePhoto = (idx) => {
    const photos = (form.photos || []).filter((_, i) => i !== idx);
    setForm({ ...form, photos, image: photos[0] || '' });
  };
  const setVariantOpt = (idx, patch) => {
    const vo = [...(form.variantOptions || [])];
    vo[idx] = { ...vo[idx], ...patch };
    setForm({ ...form, variantOptions: vo });
  };
  const save = () => {
    if (!form.name.trim() || !form.price) return toast.error('Product name and price are required');
    if (editingId) {
      updateProduct(editingId, form);
      reset();
      return;
    }
    if (usage >= plan.productLimit) return toast.error('Product limit reached', { description: `Your ${plan.name} plan allows ${plan.productLimit} products. Upgrade to add more.` });
    addProduct(merchant, form);
    reset();
  };
  const remove = (p) => {
    if (!window.confirm(`Delete product "${p.name}"?`)) return;
    removeProduct(p.id);
  };
  return (
    <div data-testid="merchant-products">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-kelate-ink">Products</h2>
          <div className="mt-1 flex items-center gap-2">
            <div className="h-1.5 w-40 overflow-hidden rounded-full bg-secondary">
              <div className="h-full rounded-full bg-kelate-wood" style={{ width: `${Math.min(100, (usage / plan.productLimit) * 100)}%` }} />
            </div>
            <span className="text-xs text-kelate-soft" data-testid="product-usage">{usage} / {plan.productLimit} products</span>
          </div>
        </div>
        <button data-testid="add-product-btn" onClick={openAdd} className="rounded-sm bg-kelate-darkwood px-4 py-2 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">
          + Add Product
        </button>
      </div>
      {showEditor && (
        <div className="mb-4 rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5" data-testid="product-editor">
          <h3 className="text-sm font-semibold text-kelate-ink">{editingId ? 'Edit Product' : 'New Product'}</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <input placeholder="Product name" data-testid="editor-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10 rounded-sm border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood" />
            <input placeholder="Price (RM)" type="number" min="0" step="0.01" data-testid="editor-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="h-10 rounded-sm border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood" />
            <input placeholder="Stock" type="number" min="0" data-testid="editor-stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="h-10 rounded-sm border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood" />
            <select data-testid="editor-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="h-10 rounded-sm border border-kelate-line bg-kelate-bg px-2 text-sm outline-none focus:border-kelate-wood">
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <div className="sm:col-span-2" data-testid="editor-photos">
              <label className="mb-1.5 block text-xs font-medium text-kelate-ink">Product photos (up to 10) <span className="font-normal text-kelate-soft">— first photo is the cover</span></label>
              <div className="flex flex-wrap gap-2">
                {(form.photos || []).map((url, i) => (
                  <div key={`${url}-${i}`} className="relative h-20 w-20 overflow-hidden rounded-xl border border-kelate-line bg-white">
                    <img src={fileUrl(url)} alt={`Photo ${i + 1}`} className="h-full w-full object-contain" data-testid={`editor-photo-${i}`} />
                    <button type="button" data-testid={`editor-photo-remove-${i}`} onClick={() => removePhoto(i)} className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80" aria-label={`Remove photo ${i + 1}`}>
                      <Trash2 size={11} />
                    </button>
                    {i === 0 && <span className="absolute bottom-0 left-0 right-0 bg-kelate-darkwood/80 py-0.5 text-center text-[9px] text-kelate-surface">Cover</span>}
                  </div>
                ))}
                {(form.photos || []).length < 10 && (
                  <ImageUpload value="" onChange={(v) => { if (v) setPhoto((form.photos || []).length, v); }} label="" testId={`editor-photo-upload-${(form.photos || []).length}`} aspect="avatar" />
                )}
              </div>
              <p className="mt-1 text-[10px] text-kelate-soft">{(form.photos || []).length} / 10 photos uploaded</p>
            </div>
            <div className="sm:col-span-2" data-testid="editor-variants">
              <label className="mb-1.5 block text-xs font-medium text-kelate-ink">Variants (optional) <span className="font-normal text-kelate-soft">— each can have its own price; leave price blank to use the base price</span></label>
              <div className="space-y-2">
                {(form.variantOptions || []).map((v, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input data-testid={`editor-variant-name-${i}`} placeholder="e.g. Small / Red / 500g" value={v.name} onChange={(e) => setVariantOpt(i, { name: e.target.value })} className="h-10 flex-1 rounded-sm border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood" />
                    <input data-testid={`editor-variant-price-${i}`} placeholder="Price RM (optional)" type="number" min="0" step="0.01" value={v.price ?? ''} onChange={(e) => setVariantOpt(i, { price: e.target.value === '' ? null : e.target.value })} className="h-10 w-36 rounded-sm border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood" />
                    <button type="button" data-testid={`editor-variant-remove-${i}`} onClick={() => setForm({ ...form, variantOptions: (form.variantOptions || []).filter((_, x) => x !== i) })} className="rounded-lg p-2 text-kelate-soft hover:bg-kelate-red/10 hover:text-kelate-red" aria-label="Remove variant">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {(form.variantOptions || []).length < 10 && (
                  <button type="button" data-testid="editor-variant-add" onClick={() => setForm({ ...form, variantOptions: [...(form.variantOptions || []), { name: '', price: null }] })} className="rounded-sm border border-dashed border-kelate-line px-3 py-2 text-xs text-kelate-soft hover:border-kelate-wood hover:text-kelate-wood">
                    + Add variant
                  </button>
                )}
              </div>
            </div>
            <textarea placeholder="Description" data-testid="editor-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm border border-kelate-line bg-kelate-bg px-3 py-2 text-sm outline-none focus:border-kelate-wood sm:col-span-2" />
          </div>
          <div className="mt-3 flex gap-2">
            <button data-testid="editor-save" onClick={save} className="rounded-sm bg-kelate-darkwood px-4 py-2 text-sm text-kelate-surface">{editingId ? 'Save Changes' : 'Publish Product'}</button>
            <button onClick={reset} className="rounded-sm border border-kelate-line px-4 py-2 text-sm text-kelate-soft">Cancel</button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-kelate-line text-left text-xs uppercase tracking-wider text-kelate-soft">
              <th className="px-4 py-3">Product</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Stock</th><th className="px-4 py-3">Sold</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-kelate-line/60 last:border-0" data-testid={`product-row-${p.slug}`}>
                <td className="px-4 py-3"><div className="flex items-center gap-2.5">{p.image ? <img src={fileUrl(p.image)} alt="" className="h-9 w-9 rounded-sm border border-kelate-line object-cover" /> : <span className="flex h-9 w-9 items-center justify-center rounded-sm border border-kelate-line bg-secondary text-xs font-semibold text-kelate-soft">{p.name[0]}</span>}<span className="font-medium text-kelate-ink">{p.name}</span></div></td>
                <td className="px-4 py-3 text-kelate-darkwood">{fmtRM2(p.price)}{(p.variantOptions || []).some((v) => v.price != null) && <span className="ml-1 text-[10px] text-kelate-soft">+</span>}</td>
                <td className="px-4 py-3">{p.stock}</td>
                <td className="px-4 py-3">{p.sold}</td>
                <td className="px-4 py-3"><StatusBadge status={p.stock < 5 ? 'DISCREPANCY' : 'READY'} testId={`stock-status-${p.slug}`} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button data-testid={`product-edit-${p.slug}`} onClick={() => openEdit(p)} className="rounded-lg p-1.5 text-kelate-soft hover:bg-secondary hover:text-kelate-ink" title="Edit"><Pencil size={14} /></button>
                    <button data-testid={`product-delete-${p.slug}`} onClick={() => remove(p)} className="rounded-lg p-1.5 text-kelate-soft hover:bg-kelate-red/10 hover:text-kelate-red" title="Delete"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MerchantFinance() {
  const { orders, payouts } = useApp();
  const merchant = useMyMerchant();
  const mine = orders.filter((o) => o.merchantId === merchant.id && !['CANCELLED'].includes(o.orderStatus));
  const gross = mine.reduce((s, o) => s + o.itemSubtotal - o.merchantDiscount, 0);
  const commission = mine.reduce((s, o) => s + o.commissionAmount, 0);
  const pending = payouts.filter((p) => p.merchantId === merchant.id && ['READY', 'PROCESSING', 'ON_HOLD'].includes(p.status)).reduce((s, p) => s + p.net, 0);
  const paid = payouts.filter((p) => p.merchantId === merchant.id && p.status === 'PAID').reduce((s, p) => s + p.net, 0);
  return (
    <div data-testid="merchant-finance" className="space-y-6">
      <div className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4 text-xs text-kelate-soft">
        Customers pay KELATE on delivery. KELATE deducts commission and settles the rest to your bank account — you never handle customer money.
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="Gross Sales" value={fmtRM2(gross)} testId="fin-gross" />
        <KpiCard label="Commission" value={fmtRM2(commission)} sub="KELATE platform fee" testId="fin-commission" />
        <KpiCard label="Pending Settlement" value={fmtRM2(pending)} testId="fin-pending" />
        <KpiCard label="Paid Out" value={fmtRM2(paid)} testId="fin-paid" />
      </div>
      <section className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-kelate-ink">Settlement Bank Account</h3>
          <button data-testid="change-bank-btn" onClick={() => toast.info('Bank detail changes require confirmation and are audit-logged.', { description: 'Contact KELATE finance to update bank details.' })} className="text-xs font-medium text-kelate-wood">Request change</button>
        </div>
        <div className="mt-2 text-sm text-kelate-ink">{merchant.bank.holder}</div>
        <div className="text-sm text-kelate-soft">{merchant.bank.bank} · <span className="font-mono" data-testid="masked-account">{merchant.bank.masked}</span></div>
      </section>
      <section className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-4">
        <h3 className="mb-3 text-sm font-semibold text-kelate-ink">Payout History</h3>
        <div className="space-y-2">
          {payouts.filter((p) => p.merchantId === merchant.id).map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-kelate-line/70 px-3 py-2.5 text-sm" data-testid={`payout-row-${p.payoutNumber}`}>
              <div>
                <span className="font-medium text-kelate-ink">{p.payoutNumber}</span>
                <span className="ml-2 text-xs text-kelate-soft">{p.orderNumbers.map((n) => `#${n}`).join(', ')}</span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-kelate-soft">Gross {fmtRM2(p.gross)} · Comm {fmtRM2(p.commission)}</span>
                <span className="font-semibold text-kelate-darkwood">{fmtRM2(p.net)}</span>
                <StatusBadge status={p.status} />
              </div>
            </div>
          ))}
          {payouts.filter((p) => p.merchantId === merchant.id).length === 0 && <p className="py-4 text-center text-sm text-kelate-soft">No payouts yet.</p>}
        </div>
      </section>
    </div>
  );
}

function MerchantSubscription() {
  const merchant = useMyMerchant();
  const { planRequests, requestPlanUpgrade } = useApp();
  const pendingReq = planRequests.find((r) => r.merchantId === merchant.id && r.status === 'PENDING');
  return (
    <div data-testid="merchant-subscription">
      <h2 className="text-base font-semibold text-kelate-ink">Subscription</h2>
      {pendingReq && (
        <div className="mt-2 rounded-2xl border border-kelate-wood/40 bg-[#F4EDE0] px-4 py-2.5 text-xs text-kelate-darkwood" data-testid="pending-upgrade-banner">
          Upgrade to <b>{pendingReq.toPlan}</b> is pending verification. KELATE will verify and approve — features unlock only after approval.
        </div>
      )}
      <p className="text-xs text-kelate-soft">Current plan: <b className="text-kelate-ink">{merchant.plan}</b> · renews monthly · annual plans pay 10 months, get 12.</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Object.values(PLANS).map((p) => (
          <div key={p.name} className={`rounded-md border p-4 ${merchant.plan === p.name ? 'border-kelate-darkwood bg-secondary/40' : 'border-kelate-line bg-kelate-surface'}`} data-testid={`sub-plan-${p.name.toLowerCase()}`}>
            <div className="flex items-center justify-between"><PlanBadge plan={p.name} />{merchant.plan === p.name && <span className="text-[10px] font-semibold uppercase text-kelate-sage">Current</span>}</div>
            <div className="mt-2 text-xl font-semibold text-kelate-darkwood">RM{p.monthly}<span className="text-xs font-normal text-kelate-soft">/mo</span></div>
            <div className="text-xs text-kelate-soft">RM{p.annual}/yr · {p.commission * 100}% commission · {p.productLimit} products</div>
            <ul className="mt-2 space-y-1 text-[11px] text-kelate-soft">
              {p.features.map((f) => <li key={f}>· {f}</li>)}
            </ul>
            {merchant.plan !== p.name && (
              <button data-testid={`upgrade-${p.name.toLowerCase()}`} disabled={!!pendingReq} onClick={() => requestPlanUpgrade(merchant.id, merchant.name, merchant.plan, p.name, 'monthly')} className="mt-3 w-full rounded-sm border border-kelate-darkwood py-1.5 text-xs font-medium text-kelate-darkwood hover:bg-kelate-darkwood hover:text-kelate-surface disabled:cursor-not-allowed disabled:opacity-40">
                {p.monthly > PLANS[merchant.plan].monthly ? 'Request Upgrade' : 'Request Switch'}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StoreSettings() {
  const { merchantProfiles, updateMerchantProfile } = useApp();
  const merchant = useMyMerchant();
  const saved = merchantProfiles[merchant.id] || {};
  const [cats, setCats] = useState(saved.categories || [merchant.category]);
  const [form, setForm] = useState({
    description: saved.description || merchant.description,
    whatsapp: saved.whatsapp || merchant.whatsapp,
    hours: saved.hours || merchant.hours,
    logo: saved.logo || merchant.logo || '',
    banner: saved.banner || merchant.banner || '',
    address: saved.address || merchant.address || '',
    mapsUrl: saved.mapsUrl || merchant.mapsUrl || '',
    merchantType: saved.merchantType || merchant.merchantType || 'PRODUCT',
    operatingHours: saved.operatingHours || merchant.operatingHours || { open: '09:00', close: '18:00', closedDays: [] },
  });
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const input = "h-10 w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";
  const toggle = (c) => setCats(cats.includes(c) ? cats.filter((x) => x !== c) : [...cats, c]);
  const save = () => {
    if (cats.length === 0) return toast.error('Choose at least one category');
    if (form.merchantType === 'FNB' && (!form.operatingHours.open || !form.operatingHours.close)) return toast.error('F&B businesses must set operating hours');
    updateMerchantProfile(merchant.id, { categories: cats, ...form });
  };
  return (
    <div className="max-w-2xl space-y-5" data-testid="merchant-store-settings">
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="text-sm font-semibold text-kelate-ink">Store Categories</h3>
        <p className="mt-0.5 text-xs text-kelate-soft">Choose all categories that describe your business. They appear on your public store and directory pages.</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`store-cat-${c.toLowerCase().replace(/[\s&]+/g, '-')}`}
              onClick={() => toggle(c)}
              className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${cats.includes(c) ? 'border-kelate-darkwood bg-kelate-darkwood text-kelate-surface' : 'border-kelate-line bg-kelate-bg text-kelate-soft hover:border-kelate-wood'}`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>
      <section className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="text-sm font-semibold text-kelate-ink">Store Details</h3>
        <div className="mt-3 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-kelate-ink">Description</label>
            <textarea data-testid="store-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full rounded-xl border border-kelate-line bg-kelate-bg px-3 py-2 text-sm outline-none focus:border-kelate-wood" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><label className="mb-1 block text-xs font-medium text-kelate-ink">WhatsApp</label><input data-testid="store-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className={input} /></div>
            <div><label className="mb-1 block text-xs font-medium text-kelate-ink">Opening Hours (display text)</label><input data-testid="store-hours" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className={input} /></div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-kelate-ink">Business type</label>
            <div className="flex flex-wrap gap-2">
              {[['FNB', 'Food & Beverage'], ['PRODUCT', 'Retail / Products'], ['SERVICE', 'Services']].map(([id, label]) => (
                <button key={id} type="button" data-testid={`store-type-${id.toLowerCase()}`} onClick={() => setForm({ ...form, merchantType: id })}
                  className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${form.merchantType === id ? 'border-kelate-darkwood bg-kelate-darkwood text-kelate-surface' : 'border-kelate-line bg-kelate-bg text-kelate-soft hover:border-kelate-wood'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div data-testid="store-operating-hours">
            <label className="mb-1 block text-xs font-medium text-kelate-ink">
              Operating hours {form.merchantType === 'FNB' && <span className="text-kelate-red">(required — customers can only order while you are open)</span>}
            </label>
            <div className="flex items-center gap-2">
              <input data-testid="store-hours-open" type="time" value={form.operatingHours.open || ''} onChange={(e) => setForm({ ...form, operatingHours: { ...form.operatingHours, open: e.target.value } })} className={input} />
              <span className="text-xs text-kelate-soft">to</span>
              <input data-testid="store-hours-close" type="time" value={form.operatingHours.close || ''} onChange={(e) => setForm({ ...form, operatingHours: { ...form.operatingHours, close: e.target.value } })} className={input} />
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {DAYS.map((d, i) => (
                <button key={d} type="button" data-testid={`store-closed-day-${d.toLowerCase()}`}
                  onClick={() => {
                    const cd = form.operatingHours.closedDays || [];
                    setForm({ ...form, operatingHours: { ...form.operatingHours, closedDays: cd.includes(i) ? cd.filter((x) => x !== i) : [...cd, i] } });
                  }}
                  className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${(form.operatingHours.closedDays || []).includes(i) ? 'border-kelate-red bg-kelate-red/10 text-kelate-red' : 'border-kelate-line bg-kelate-bg text-kelate-soft hover:border-kelate-wood'}`}>
                  {d} off
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-kelate-ink">Shop Address (pickup point)</label>
            <input data-testid="store-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={input} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-kelate-ink">Google Maps link (Visit Store button)</label>
            <input data-testid="store-maps-url" placeholder="https://maps.google.com/... or https://maps.app.goo.gl/..." value={form.mapsUrl} onChange={(e) => setForm({ ...form, mapsUrl: e.target.value })} className={input} />
            <p className="mt-1 text-[11px] text-kelate-soft">Open Google Maps, find your shop, tap Share → Copy link, and paste it here. Customers will see a "Visit Store" button on your page.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <ImageUpload value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} label="Store logo" testId="store-logo-upload" aspect="avatar" hint="Square logo, shown on your store" />
            <ImageUpload value={form.banner} onChange={(v) => setForm({ ...form, banner: v })} label="Cover photo" testId="store-cover-upload" aspect="cover" hint="Wide banner shown at the top of your store page" />
          </div>
        </div>
      </section>
      <button data-testid="store-save" onClick={save} className="rounded-xl bg-kelate-darkwood px-6 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Save Store Profile</button>
    </div>
  );
}

function LockedFeature({ feature, requiredPlan }) {
  const p = PLANS[requiredPlan];
  return (
    <div className="flex flex-col items-center rounded-3xl border border-dashed border-kelate-line bg-kelate-surface px-6 py-16 text-center shadow-[0_1px_4px_rgba(81,68,55,0.06)]" data-testid={`locked-${feature.toLowerCase()}`}>
      <Lock size={32} className="text-kelate-line" strokeWidth={1.5} />
      <h3 className="mt-4 text-lg font-semibold text-kelate-ink">{feature} is available on {requiredPlan}</h3>
      <p className="mt-1 max-w-sm text-sm text-kelate-soft">{requiredPlan} · RM{p.monthly}/month · {p.features.slice(1, 3).join(' · ')} · {p.commission * 100}% commission</p>
      <Link to="/merchant-center/subscription" data-testid="locked-upgrade-btn" className="mt-4 rounded-sm bg-kelate-darkwood px-6 py-2.5 text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Request Upgrade</Link>
    </div>
  );
}

function MarketingTools() {
  const merchant = useMyMerchant();
  const { wallets, customProducts, promoteProduct, vouchers, createVoucher, toggleVoucher } = useApp();
  const balance = wallets[merchant.id] || 0;
  const myProducts = customProducts.filter((p) => p.merchantId === merchant.id);
  const promotable = myProducts.filter((p) => !(p.badges || []).includes('FEATURED'));
  const myVouchers = vouchers.filter((v) => v.scope === 'MERCHANT' && v.merchantId === merchant.id);
  const [promoteId, setPromoteId] = useState('');
  const [vform, setVform] = useState({ code: '', type: 'FIXED', value: '', minSpend: 0 });
  const input = "h-10 rounded-xl border border-kelate-line bg-kelate-bg px-3 text-sm outline-none focus:border-kelate-wood";
  const submitVoucher = () => {
    createVoucher({ code: vform.code, type: vform.type, value: vform.value, minSpend: vform.minSpend, scope: 'MERCHANT', merchantId: merchant.id, merchantName: merchant.name });
    setVform({ code: '', type: 'FIXED', value: '', minSpend: 0 });
  };
  return (
    <div className="space-y-4" data-testid="marketing-tools">
      <div className="rounded-3xl bg-kelate-darkwood p-5 text-kelate-surface shadow-[0_8px_24px_rgba(81,68,55,0.2)]">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-kelate-wood">Ads Wallet Balance</div>
            <div className="mt-1 text-3xl font-bold" data-testid="ads-wallet-balance">{fmtRM2(balance)}</div>
          </div>
          <Megaphone size={28} className="text-kelate-wood" strokeWidth={1.5} />
        </div>
        <p className="mt-1 text-xs text-[#CBBFA9]">Top up to promote products — RM20 per boost gives a product Featured placement across KELATE.</p>
        <details className="mt-3">
          <summary data-testid="ads-topup-open" className="cursor-pointer rounded-xl bg-kelate-surface/10 px-3 py-2 text-xs font-medium text-kelate-surface">Top up ads wallet</summary>
          <div className="mt-3 rounded-xl bg-kelate-surface p-4 text-kelate-ink">
            <TopUpForm ownerType="merchant" ownerKey={merchant.id} ownerName={merchant.name} />
          </div>
        </details>
        <div className="mt-3 flex gap-2">
          <select data-testid="promote-product-select" value={promoteId} onChange={(e) => setPromoteId(e.target.value)} className={`${input} flex-1 bg-kelate-surface`}>
            <option value="">Select a product to promote (RM20)</option>
            {promotable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button data-testid="promote-product-btn" onClick={() => promoteId && promoteProduct(merchant.id, promoteId, myProducts.find((p) => p.id === promoteId)?.name)} disabled={!promoteId || promotable.length === 0} className="rounded-xl bg-kelate-surface px-4 text-xs font-semibold text-kelate-darkwood hover:bg-kelate-bg disabled:opacity-40">
            Promote
          </button>
        </div>
      </div>
      <div className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="text-sm font-semibold text-kelate-ink">Store Vouchers</h3>
        <p className="mt-1 text-xs text-kelate-soft">Create vouchers buyers apply at checkout on your products. Merchant-funded vouchers reduce your commission base (and your settlement).</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-5">
          <input data-testid="mv-code" placeholder="CODE (e.g. BAKER5)" value={vform.code} onChange={(e) => setVform({ ...vform, code: e.target.value.toUpperCase() })} className={input} />
          <select data-testid="mv-type" value={vform.type} onChange={(e) => setVform({ ...vform, type: e.target.value })} className={input}>
            <option value="FIXED">RM off</option>
            <option value="PERCENT">% off</option>
          </select>
          <input data-testid="mv-value" type="number" min="0" placeholder="Value" value={vform.value} onChange={(e) => setVform({ ...vform, value: e.target.value })} className={input} />
          <input data-testid="mv-min-spend" type="number" min="0" placeholder="Min spend RM" value={vform.minSpend} onChange={(e) => setVform({ ...vform, minSpend: e.target.value })} className={input} />
          <button data-testid="mv-create" onClick={submitVoucher} className="rounded-xl bg-kelate-darkwood text-sm font-medium text-kelate-surface hover:bg-kelate-wood">Create</button>
        </div>
        <div className="mt-3 space-y-2">
          {myVouchers.length === 0 && <p className="text-xs text-kelate-soft" data-testid="mv-empty">No store vouchers yet.</p>}
          {myVouchers.map((v) => (
            <div key={v.code} data-testid={`mv-row-${v.code}`} className="flex items-center justify-between rounded-2xl border border-kelate-line/60 bg-kelate-bg px-4 py-2.5 text-xs">
              <span className="font-mono font-bold text-kelate-darkwood">{v.code}</span>
              <span className="text-kelate-soft">{v.type === 'PERCENT' ? `${v.value}% off` : `${fmtRM2(v.value)} off`}{v.minSpend > 0 ? ` · min ${fmtRM2(v.minSpend)}` : ''}</span>
              <button data-testid={`mv-toggle-${v.code}`} onClick={() => toggleVoucher(v.code)} className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide ${v.active ? 'bg-kelate-sage/15 text-kelate-sage' : 'bg-secondary text-kelate-soft'}`}>{v.active ? 'Active' : 'Off'}</button>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-3xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)]">
        <h3 className="text-sm font-semibold text-kelate-ink">Featured Placement</h3>
        <p className="mt-1 text-xs text-kelate-soft">BRAND merchants receive top placement on Home and Marketplace. Campaign participation is managed by KELATE marketing — check Admin → Marketing for the active campaign.</p>
      </div>
    </div>
  );
}

function NotificationSettings() {
  const { merchantNotif, setMerchantNotif } = useApp();
  const [snd, setSnd] = useState(soundEnabled());
  const rows = [
    ['newOrder', 'New Order'], ['payment', 'Payment Alerts'], ['pickup', 'Pickup Alerts'],
    ['payout', 'Payout Alerts'], ['reviews', 'Reviews'], ['marketing', 'Marketing'], ['system', 'System'],
  ];
  return (
    <div className="max-w-lg space-y-4" data-testid="merchant-notif-settings">
      <div className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-kelate-ink">Sound Notifications</h3>
            <p className="text-xs text-kelate-soft">Hear alerts the moment something happens.</p>
          </div>
          <div className="flex items-center gap-2">
            {snd ? <Volume2 size={16} className="text-kelate-wood" /> : <VolumeX size={16} className="text-kelate-soft" />}
            <Switch data-testid="sound-master-switch" checked={snd} onCheckedChange={(v) => { setSnd(v); setSoundEnabled(v); }} />
          </div>
        </div>
        <button data-testid="test-sound-btn" onClick={() => playTest(VOLUME_LEVELS[merchantNotif.volume])} className="mt-3 rounded-sm border border-kelate-darkwood px-3 py-1.5 text-xs font-medium text-kelate-darkwood hover:bg-secondary">
          Test Sound
        </button>
      </div>
      <div className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5">
        <h3 className="text-sm font-semibold text-kelate-ink">Alert Types</h3>
        <div className="mt-3 space-y-3">
          {rows.map(([key, label]) => (
            <div key={key} className="flex items-center justify-between text-sm">
              <span className="text-kelate-ink">{label}</span>
              <Switch data-testid={`notif-${key}`} checked={merchantNotif[key]} onCheckedChange={(v) => setMerchantNotif({ [key]: v })} />
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-5">
        <h3 className="text-sm font-semibold text-kelate-ink">Volume</h3>
        <div className="mt-2 flex gap-2">
          {['low', 'medium', 'high'].map((v) => (
            <button key={v} data-testid={`volume-${v}`} onClick={() => setMerchantNotif({ volume: v })} className={`rounded-sm px-4 py-1.5 text-sm capitalize ${merchantNotif.volume === v ? 'bg-kelate-darkwood text-kelate-surface' : 'border border-kelate-line text-kelate-soft'}`}>{v}</button>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-kelate-line pt-4 text-sm">
          <span className="text-kelate-ink">Desktop Notifications</span>
          <Switch data-testid="desktop-notif" checked={merchantNotif.desktop} onCheckedChange={(v) => {
            if (v && window.Notification?.permission !== 'granted') window.Notification?.requestPermission();
            setMerchantNotif({ desktop: v });
          }} />
        </div>
      </div>
    </div>
  );
}

function Stub({ title, children }) {
  return (
    <div className="rounded-3xl bg-kelate-surface shadow-[0_1px_4px_rgba(81,68,55,0.08)] p-6 text-sm text-kelate-soft" data-testid={`stub-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <h3 className="text-base font-semibold text-kelate-ink">{title}</h3>
      <p className="mt-1">{children || 'This section is part of the full KELATE roadmap. The prototype focuses on the order → delivery → payout lifecycle.'}</p>
    </div>
  );
}

export default function MerchantCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { orders, unreadCount } = useApp();
  const merchant = useMyMerchant();
  const plan = merchant.plan;
  if (user === null) return null;
  if (!user || user.role !== 'merchant') { navigate('/account/login?redirect=/merchant-center'); return null; }
  const newOrders = orders.filter((o) => o.merchantId === merchant.id && o.orderStatus === 'PENDING').length;

  const groups = [
    { label: 'Overview', items: [{ to: '/merchant-center', icon: LayoutGrid, label: 'Dashboard', end: true }] },
    {
      label: 'Store', items: [
        { to: '/merchant-center/store', icon: Store, label: 'Store' },
        { to: '/merchant-center/products', icon: Package, label: 'Products' },
        { to: '/merchant-center/orders', icon: ClipboardList, label: 'Orders', badge: newOrders },
        { to: '/merchant-center/reviews', icon: Star, label: 'Reviews' },
      ],
    },
    {
      label: 'Grow', items: [
        { to: '/merchant-center/marketing', icon: Megaphone, label: 'Marketing' },
        { to: '/merchant-center/analytics', icon: BarChart3, label: 'Analytics' },
      ],
    },
    {
      label: 'Business', items: [
        { to: '/merchant-center/messages', icon: MessageSquare, label: 'Messages' },
        { to: '/merchant-center/finance', icon: Wallet, label: 'Finance' },
        { to: '/merchant-center/staff', icon: Users, label: 'Staff' },
      ],
    },
    {
      label: 'Account', items: [
        { to: '/merchant-center/subscription', icon: CreditCard, label: 'Subscription' },
        { to: '/merchant-center/settings/notifications', icon: Bell, label: 'Notifications', badge: unreadCount('merchant', merchant?.id) },
        { to: '/merchant-center/account', icon: Settings, label: 'Account' },
        { to: '/merchant-center/help', icon: LifeBuoy, label: 'Help & Support' },
      ],
    },
  ];

  return (
    <CenterLayout title="Merchant Center" groups={groups} recipientType="merchant" recipientId={merchant.id} fullNotifUrl="/merchant-center/settings/notifications">
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="orders" element={plan === 'LIST' ? <LockedFeature feature="Orders" requiredPlan="STORE" /> : <MerchantOrders />} />
        <Route path="products" element={plan === 'LIST' ? <LockedFeature feature="Products" requiredPlan="STORE" /> : <MerchantProducts />} />
        <Route path="finance" element={<MerchantFinance />} />
        <Route path="subscription" element={<MerchantSubscription />} />
        <Route path="marketing" element={['BOOST', 'BRAND'].includes(plan) ? <MarketingTools /> : <LockedFeature feature="Marketing" requiredPlan="BOOST" />} />
        <Route path="settings/notifications" element={<NotificationSettings />} />
        <Route path="store" element={<StoreSettings />} />
        <Route path="reviews" element={<Stub title="Reviews">Customer reviews for your products will appear here.</Stub>} />
        <Route path="analytics" element={<Stub title="Analytics">Advanced analytics are available on BOOST and BRAND plans.</Stub>} />
        <Route path="messages" element={<Stub title="Messages" />} />
        <Route path="staff" element={<Stub title="Staff" />} />
        <Route path="account" element={<AccountSettings testIdPrefix="merchant-account" />} />
        <Route path="help" element={<Support />} />
      </Routes>
    </CenterLayout>
  );
}
